// pages/UserAdmin/TemplateVariableMapDialog.js
//
// Onde o supervisor diz de onde sai o valor de cada {{n}} na hora do envio.
//
// O formulário de criação também pergunta isso (mesmo componente de
// seleção, ver VariableSourcePicker). Este diálogo existe porque nome de
// template é imutável na Meta: sem ele, os templates que o cliente JÁ tem em
// produção nunca poderiam ser mapeados, e mudar o mapeamento depois exigiria
// recriar o template.
//
// A tela desenha uma linha por variável QUE O TEMPLATE TEM HOJE — a lista
// vem do backend junto com o mapeamento salvo, não do que está guardado. Se
// o template mudou na Meta, é a realidade de agora que manda.

import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import { FiSave } from 'react-icons/fi';
import api from '../../services/Api';
import VariableSourcePicker, { incompleteEntries, slotOf }
  from './VariableSourcePicker';

const SYNE = "'Syne', system-ui, sans-serif";

export default function TemplateVariableMapDialog({
  open, onClose, deviceId, template, onSaved,
}) {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [mapping, setMapping] = useState([]);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    if (!deviceId || !template?.name) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(
        `/templates/variable-map/${deviceId}/${encodeURIComponent(template.name)}`,
        { ...auth, params: { language: template.language } }
      );
      setData(res.data);
      setMapping(res.data.mapping || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível carregar o mapeamento.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, template?.name, template?.language, token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const update = (slot, next) =>
    setMapping((prev) => prev.map((e) => (slotOf(e) === slot ? next : e)));

  const incompletos = incompleteEntries(mapping);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(
        `/templates/variable-map/${deviceId}/${encodeURIComponent(template.name)}`,
        { language: data?.language || template.language, mapping },
        auth
      );
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível salvar o mapeamento.');
    } finally {
      setSaving(false);
    }
  };

  const semVariavel = data && !Object.values(data.var_counts || {}).some(Boolean);
  const semCampos = (data?.custom_fields || []).length === 0;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800, color: '#0f172a', pb: 1 }}>
        Mapear variáveis
        <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontWeight: 500 }}>
          {template?.name} · {data?.language || template?.language}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {data?.stale && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontSize: '0.82rem' }}>
            O template mudou na Meta depois que este mapeamento foi salvo — a
            quantidade de variáveis não bate mais. Revise linha por linha
            antes de gravar.
          </Alert>
        )}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={22} sx={{ color: '#25d366' }} />
          </Stack>
        ) : semVariavel ? (
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.85rem' }}>
            Este template não tem variáveis. Nada a mapear — ele pode ser
            enviado direto.
          </Alert>
        ) : (
          <>
            {data?.body_text && (
              <Box sx={{
                bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2,
                p: 1.5, mb: 2.5, fontSize: '0.85rem', color: '#475569',
                whiteSpace: 'pre-wrap',
              }}>
                {data.body_text}
              </Box>
            )}

            {semCampos && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontSize: '0.8rem' }}>
                Sua empresa ainda não tem campos personalizados. Crie em
                Contatos → Campos personalizados para poder puxar dados do CRM
                aqui (ex.: valor do boleto).
              </Alert>
            )}

            <Stack spacing={2}>
              {mapping.map((entry) => (
                <Box key={slotOf(entry)} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 1.75 }}>
                  <VariableSourcePicker
                    entry={entry}
                    customFields={data?.custom_fields || []}
                    disabled={saving}
                    onChange={(next) => update(slotOf(entry), next)}
                  />
                </Box>
              ))}
            </Stack>

            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 2 }}>
              Quando o contato não tiver o dado, a tela de envio pergunta antes
              de disparar — nada sai com a variável em branco.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9' }}>
        <Button onClick={onClose} disabled={saving}
                sx={{ textTransform: 'none', fontFamily: SYNE, fontWeight: 700, color: '#64748b' }}>
          Cancelar
        </Button>
        {!semVariavel && (
          <Button
            onClick={handleSave}
            disabled={saving || loading || incompletos.length > 0}
            variant="contained" disableElevation
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <FiSave size={14} />}
            sx={{
              textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
              bgcolor: '#25d366', color: '#0f172a', borderRadius: 2,
              '&:hover': { bgcolor: '#16a34a', color: '#fff' },
              '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
            }}
          >
            {saving ? 'Salvando…'
              : incompletos.length ? `Faltam ${incompletos.length}` : 'Salvar mapeamento'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
